<template>
  <div ref="timeline-container" class="timeline-container"></div>
</template>

<script>
  import Vue from 'vue'
  import {Timeline, DataSet} from 'vis';
  import {isEqual, merge} from 'lodash'
  import 'vis/dist/vis.css'

  export default {
    name: "timeline",
    props: {
      initTime: {
        type: Number,
        default: new Date().getTime()
      },
      zoomMax: {
        type: Number,
        default: 24 * 60 * 60 * 1000
      },
      zoomMin: {
        type: Number,
        default: 60 * 1000
      },
      initRange: {
        type: Object,
        default: null
      },
      entities: {
        type: Array,
        default: () => []
      },
      entityPromiseFun: {
        type: Function,
        default: (entity) => Promise.resolve(entity)
      },
      cesiumObject: {
        type: String,
        default: 'default'
      },
    },
    data() {
      return {
        currentTime: this.initTime,
        multiplier: 1,
        range: this.initRange || {
          start: this.initTime - this.zoomMax / 2,
          end: this.initTime + this.zoomMax / 2
        },
        timeline: null,
        timelineEntities: []
      }
    },

    mounted() {

      const container = this.$refs['timeline-container'];
      const options = {};

      this.timeline = new Timeline(container, new DataSet([]), options);

      this.updateOptions();

      this.$bus.$on('clockTick', e => {

        const time = e.currentTime;
        this.currentTime = time;
        this.multiplier = e.multiplier;

        this.timeline.setCurrentTime(time);

        if (time < this.range.start || time > this.range.end)
          this.$emit("currentTimeChanged", new Date((this.multiplier > 0) ? this.range.start : this.range.end))
      });

      this.timeline.on('click', e => {

        if (e.item == null) {
          this.$emit("currentTimeChanged", new Date(e.time));
        } else {
          const item = this.timeline.itemsData._data[e.item];
          this.$emit("currentTimeChanged", new Date(item.start));

          if (item.position)
            this.getViewer().camera.flyTo({
              destination: Cesium.Cartesian3.fromRadians(item.position.lng, item.position.lat, item.position.alt)
            });
        }
      });

      this.timeline.on('rangechanged', ({start, end}) => {

        const startTime = new Date(start).getTime();
        const endTime = new Date(end).getTime();

        this.range = {
          start: startTime,
          end: endTime
        };
      });

      this.updateTimeLineEntities(this.entities, []);
    },
    computed: {},
    methods: {
      updateItemsForEntities(entities) {
        const items = entities.map(({id, label, startTime, position}) => {
          return {id, content: label, start: startTime, position}
        });
        if (this.timeline)
          this.timeline.setItems(new DataSet(items));
      },
      updateTimeLineEntities(newEntities, oldEntities) {

        const entitiesForAdd = newEntities.filter(newEntity => !oldEntities.find(oldEntity => isEqual(newEntity, oldEntity)));
        const entitiesForRemove = (oldEntities || []).filter(oldEntity => !newEntities.find(newEntity => newEntity.id === oldEntity.id)).map(entity => entity.id);

        if (entitiesForRemove.length)
          this.timelineEntities = this.timelineEntities.filter(entity => !entitiesForRemove.includes(entity.id));

        entitiesForAdd.forEach(entity => {
          this.entityPromiseFun(entity).then(timeLineEntity => {
            this.timelineEntities.push(timeLineEntity)
          });
        })
      },
      updateOptions() {
        this.timeline.setOptions(
          {
            start: this.range.start,
            end: this.range.end,
            stack: false,
            zoomMax: this.zoomMax,
            zoomMin: this.zoomMin
          }
        );
      },

      getViewer() {
        return window.cesiumViewer[this.cesiumObject].viewer;
      }
    },

    components: {},

    watch: {
      entities: {
        immediate: true,
        handler(newEntities, oldEntities) {
          this.updateTimeLineEntities(newEntities, oldEntities);
        }
      },
      timelineEntities(newEntities) {
        this.updateItemsForEntities(newEntities);
      },
      range(newVal, oldVal) {
        if (newVal.start !== oldVal.start || newVal.end !== oldVal.end) {
          this.$emit("currentIntervalChanged", newVal);
        }
      }
    }
  }
</script>

<style>
  .timeline-container .vis-timeline.vis-bottom {
    border-bottom-width: 0 !important;
    border-left-width: 0 !important;
  }

  .timeline-container .vis-timeline .vis-text {
    color: #7C7C7C !important;
  }
</style>

<style scoped>
  .timeline-container {
    z-index: 10000;
    background: rgba(27, 28, 29, 0.8);
    position: absolute;
    bottom: 0;
    left: 160pt;
    width: calc(100% - 340pt);
  }

  .timeline-container :global .vis-timeline {
    border: 0;
  }

  .timeline-container :global .vis-time-axis .vis-text {
    color: #afafaf;
    font-weight: 100;
    font-family: sans-serif;
  }

  .timeline-container :global .vis-panel.vis-center {
    border-top-style: none;
    border-bottom-style: none;
  }

  .timeline-container :global .vis-panel.vis-left {
    border-top-style: none;
    border-bottom-style: none;
  }

  .timeline-container :global .vis-panel.vis-right {
    border-top-style: none;
    border-bottom-style: none;
  }

  .timeline-container :global .vis-time-axis .vis-grid.vis-minor {
    border-color: #afafaf;
  }

  .timeline-container :global .vis-content {
    height: 0;
    overflow: visible;
  }

  .timeline-container :global .vis-timeline {
    overflow: visible;
  }

  .timeline-container :global .vis-panel.vis-center {
    overflow: visible;
  }

  .timeline-container :global .vis-item {
    cursor: pointer;

    background-color: rgba(0, 0, 0, 0.4);
    color: #afafaf;
    border-color: rgba(0, 0, 0, 0.8);
  }

  .timeline-container :global .vis-item.vis-selected {
    background-color: rgba(200, 200, 200, 0.6);
    color: #afafaf;
    border-color: rgba(0, 0, 0, 1);
    z-index: 0;
  }

  .timeline-container :global .vis-item.vis-box {
    top: -50px !important;
    border-radius: 100px;
  }

  .timeline-container :global .vis-item.vis-line {
    top: -15px !important;
    height: 15px;
    border: 3px rgba(0, 0, 0, 0.5);
  }

  .timeline-container :global .vis-item.vis-line.vis-selected {
    background-color: rgba(0, 0, 0, 0.4);
    border-color: rgba(0, 0, 0, 0.8);
  }

  .timeline-container :global .vis-item.vis-dot {
    display: none;
  }
</style>
